import {
  CaretDownOutlined,
  CaretRightOutlined,
  CaretUpOutlined,
  FileImageOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  CheckOutlined,
  FileTextOutlined
} from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { Skeleton, Spin, Tooltip } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { PageGrid } from '../../../components/page/page-grid'
import cx from 'classnames'
import dayjs from 'dayjs'
import type { RenderExpandIconProps } from 'rc-table/lib/interface'
import { useCallback, useMemo, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import $api, { API_URL } from '../../../API'
import { PageContainer, PageHeader } from '../../../components/page'
import type {
  DetailedLockboxCheckResponse,
  LockboxCheck,
  LockboxChecksResponse,
  LockboxChecksSummaryResponse
} from './lockbox-types'
import * as previewActions from '../../../store/previewer'

import './lockbox.css'
import { useAppDispatch, useAppSelector } from '../../../store'
import type { Attachment } from '../../../store/previewer/types'
import { Role } from '../../../domain/role.enum'

import './lockbox.css'

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD'
})

const getSecuredAccountNumber = (accountNumber: string): string => {
  return `****${accountNumber.slice(-4)}`
}

export function LockboxDetailsPage() {
  const params = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const [visibleAccountNumbers, setVisibleAccountNumbers] = useState<Set<string>>(new Set())
  const [isBankAccountNumberVisible, setIsBankAccountNumberVisible] = useState(false)
  const [copiedAccountId, setCopiedAccountId] = useState<string | null>(null)
  const [copiedBankAccount, setCopiedBankAccount] = useState(false)

  const user = useAppSelector(state => state.app.user)

  const dispatch = useAppDispatch()

  const hasAdminRole = useMemo(() => {
    return user.roles.includes(Role.Administrator)
  }, [user.roles])

  const handleCopyAccountNumber = useCallback(async (accountNumber: string, recordId: string) => {
    try {
      await navigator.clipboard.writeText(accountNumber)
      setCopiedAccountId(recordId)
      setTimeout(() => setCopiedAccountId(null), 2000)
    } catch {
      // Silent fail
    }
  }, [])

  const handleCopyBankAccount = useCallback(async (accountNumber: string) => {
    try {
      await navigator.clipboard.writeText(accountNumber)
      setCopiedBankAccount(true)
      setTimeout(() => setCopiedBankAccount(false), 2000)
    } catch {
      // Silent fail
    }
  }, [])

  const handlePdfPreviewClick = useCallback(
    (file: Attachment) => (): void => {
      dispatch(previewActions.open(file))
    },
    [dispatch]
  )

  const handlePropagatedActionClick = useCallback((event: React.MouseEvent<HTMLElement>): void => {
    event.stopPropagation()
  }, [])

  const lockboxChecksSummaryQuery = useQuery<LockboxChecksSummaryResponse>({
    queryKey: [
      'lockbox-checks-summary',
      {
        lockboxId: params.id,
        date: searchParams.get('date')
      }
    ],

    queryFn: async ({ queryKey }) => {
      const { lockboxId, date } = queryKey[1] as typeof queryKey extends Array<string | infer T>
        ? T
        : never

      const searchParams = new URLSearchParams([['date', date]])

      const result = await $api.get<LockboxChecksSummaryResponse>(
        `pinnacle/lockboxes/${lockboxId}/checks/summary?${searchParams.toString()}`
      )

      return result.data
    },

    enabled: params.id !== undefined
  })

  const lockboxChecksQuery = useQuery<LockboxChecksResponse>({
    queryKey: [
      'lockbox-checks',
      {
        lockboxId: params.id,
        date: searchParams.get('date')
      }
    ],

    queryFn: async ({ queryKey }) => {
      const { lockboxId, date } = queryKey[1] as typeof queryKey extends Array<string | infer T>
        ? T
        : never

      const searchParams = new URLSearchParams([['date', date]])

      const result = await $api.get<LockboxChecksResponse>(
        `pinnacle/lockboxes/${lockboxId}/checks?${searchParams.toString()}`
      )

      return result.data
    },

    enabled: params.id !== undefined
  })

  const token = window.localStorage.getItem('token')

  const buildFileHref = useCallback(
    (checkId: string, type: 'images' | 'origins') => {
      if (type === 'images') {
        return `${API_URL}/pinnacle/checks/${checkId}/images/preview?date=${searchParams.get('date')}&token=${encodeURIComponent(token ?? '')}`
      }

      return `${API_URL}/pinnacle/checks/${checkId}/origins/preview?date=${searchParams.get('date')}&token=${encodeURIComponent(token ?? '')}`
    },
    [token, searchParams]
  )

  const columns = useMemo<ColumnsType<LockboxCheck>>(
    () => [
      {
        title: 'Batch #',
        dataIndex: 'originalBatchId',
        width: 120
      },
      {
        title: 'Transaction #',
        dataIndex: 'originalTransactionId',
        width: 120
      },
      {
        title: 'Check #',
        dataIndex: 'checkNumber',
        width: 120
      },
      {
        title: 'Routing #',
        dataIndex: 'routingTransit',
        width: 120
      },
      {
        title: 'Account #',
        dataIndex: 'accountNumber',
        width: 180,
        align: 'right',
        render: (accountNumber: string, record: LockboxCheck) => {
          const isVisible = visibleAccountNumbers.has(record.id)
          const displayValue = isVisible ? accountNumber : getSecuredAccountNumber(accountNumber)

          return (
            <div className='flex gap-2 items-center justify-end'>
              <Tooltip
                title={
                  copiedAccountId === record.id ? (
                    <span>
                      <CheckOutlined /> Copied
                    </span>
                  ) : isVisible ? (
                    'Click to copy'
                  ) : (
                    ''
                  )
                }
              >
                <span
                  className={`w-[150px] inline-block text-right ${
                    isVisible ? 'cursor-pointer hover:text-blue-600 transition-colors' : ''
                  }`}
                  onClick={e => {
                    if (isVisible) {
                      e.stopPropagation()
                      handleCopyAccountNumber(accountNumber, record.id)
                    } else {
                      handlePropagatedActionClick(e)
                    }
                  }}
                >
                  {displayValue}
                </span>
              </Tooltip>
              <button
                type='button'
                onClick={e => {
                  e.stopPropagation()
                  setVisibleAccountNumbers(prev => {
                    const next = new Set(prev)

                    if (isVisible) {
                      next.delete(record.id)
                    } else {
                      next.add(record.id)
                    }

                    return next
                  })
                }}
                className='flex justify-center items-center transition-colors text-slate-400 hover:text-slate-700'
              >
                {isVisible ? (
                  <EyeInvisibleOutlined className='text-lg' />
                ) : (
                  <EyeOutlined className='text-lg' />
                )}
              </button>
            </div>
          )
        }
      },
      {
        title: 'Checks Total',
        dataIndex: 'checkAmount',
        width: 120,
        align: 'right',
        render: (val: number) => currencyFormatter.format(val)
      },
      {
        key: 'actions',
        title: 'Actions',
        align: 'center',
        width: 150,
        render: (_value, record) => (
          <div
            className='flex gap-2 justify-center items-center'
            onClick={handlePropagatedActionClick}
          >
            <Tooltip title='View All Images'>
              <button
                type='button'
                onClick={handlePdfPreviewClick({
                  mimeType: 'image/tiff',
                  name: 'All Images',
                  href: buildFileHref(record.id, 'images')
                })}
                className='w-7 h-7 flex items-center justify-center hover:bg-blue-100 rounded-md transition-colors border border-blue-200 bg-blue-50'
              >
                <FileImageOutlined className='text-sm text-blue-600' />
              </button>
            </Tooltip>

            {hasAdminRole && (
              <Tooltip title='View XML Data'>
                <button
                  type='button'
                  onClick={handlePdfPreviewClick({
                    mimeType: 'text/xml',
                    name: 'XML Data',
                    href: buildFileHref(record.id, 'origins')
                  })}
                  className='w-7 h-7 flex items-center justify-center hover:bg-slate-200 rounded-md transition-colors border border-slate-300 bg-slate-100'
                >
                  <FileTextOutlined className='text-sm text-slate-600' />
                </button>
              </Tooltip>
            )}
          </div>
        )
      }
    ],
    [
      buildFileHref,
      handlePdfPreviewClick,
      hasAdminRole,
      handlePropagatedActionClick,
      visibleAccountNumbers,
      copiedAccountId,
      handleCopyAccountNumber
    ]
  )

  const totalAmountDiff = useMemo(() => {
    return (
      (lockboxChecksSummaryQuery.data?.bank?.totalAmount ?? 0) -
      (lockboxChecksSummaryQuery.data?.totalBatchCheckAmount ?? 0)
    )
  }, [lockboxChecksSummaryQuery.data])

  const pageDescription = useMemo(() => {
    if (lockboxChecksSummaryQuery.isLoading) {
      return <Skeleton.Node active className='!h-4 rounded' />
    }

    if (lockboxChecksSummaryQuery.isError) {
      return null
    }

    const accountNumber = lockboxChecksSummaryQuery.data?.bank?.accountNumber ?? ''
    const state = lockboxChecksSummaryQuery.data?.bank?.state ?? ''
    const displayAccountNumber = isBankAccountNumberVisible
      ? accountNumber
      : getSecuredAccountNumber(accountNumber)

    return (
      <div className='flex gap-2 items-center'>
        <Tooltip
          title={
            copiedBankAccount ? (
              <span>
                <CheckOutlined /> Copied
              </span>
            ) : isBankAccountNumberVisible ? (
              'Click to copy'
            ) : (
              ''
            )
          }
        >
          <span
            className={`${isBankAccountNumberVisible ? 'cursor-pointer hover:text-blue-600 transition-colors' : ''}`}
            onClick={() => {
              if (isBankAccountNumberVisible) {
                handleCopyBankAccount(accountNumber)
              }
            }}
          >
            {state} • {displayAccountNumber}
          </span>
        </Tooltip>

        {accountNumber && (
          <button
            type='button'
            onClick={() => setIsBankAccountNumberVisible(prev => !prev)}
            className='flex justify-center items-center transition-colors text-slate-400 hover:text-slate-700'
          >
            {isBankAccountNumberVisible ? (
              <EyeInvisibleOutlined className='text-lg' />
            ) : (
              <EyeOutlined className='text-lg' />
            )}
          </button>
        )}
      </div>
    )
  }, [
    lockboxChecksSummaryQuery.isLoading,
    lockboxChecksSummaryQuery.data?.bank?.state,
    lockboxChecksSummaryQuery.data?.bank?.accountNumber,
    lockboxChecksSummaryQuery.isError,
    isBankAccountNumberVisible,
    copiedBankAccount,
    handleCopyBankAccount
  ])

  return (
    <PageContainer className='space-y-6 w-full lockbox-page'>
      <div className='flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4 lg:gap-6'>
        <PageHeader
          title={`Lockbox ${params.id} Details`}
          breadcrumbs={[
            {
              key: 'lockbox',
              label: 'Lockbox Checks',
              to: '/integrations/pinnacle/lockbox-checks'
            },
            { key: 'lockbox-details', label: 'Lockbox Details' }
          ]}
          description={pageDescription}
        />

        {lockboxChecksSummaryQuery.isLoading && (
          <Skeleton.Node active className='!w-auto !h-[80px] rounded' />
        )}

        {lockboxChecksSummaryQuery.isError && (
          <div className='flex items-center h-[80px] p-4 rounded bg-slate-50'>
            <span className='text-slate-600'>Lockbox not found, try again later</span>
          </div>
        )}

        {!lockboxChecksSummaryQuery.isLoading &&
          !lockboxChecksSummaryQuery.isError &&
          lockboxChecksSummaryQuery.data && (
            <div className='flex flex-row flex-wrap gap-4 lg:gap-8 rounded-lg bg-gray-100 px-4 lg:px-8 py-2 shrink-0'>
              <div className='flex flex-col justify-start items-center'>
                <div className='font-lato tracking-wide text-[9.5px] uppercase text-gray-500'>
                  Deposit Date
                </div>
                <div className='font-lato text-base lg:text-lg font-semibold text-gray-700 whitespace-nowrap'>
                  {dayjs(lockboxChecksSummaryQuery.data.depositDate).format('MM/DD/YYYY')}
                </div>
              </div>

              <div className='flex flex-col justify-start items-center'>
                <div className='font-lato tracking-wide text-[9.5px] uppercase text-gray-500'>
                  Transactions
                </div>
                <div className='font-lato text-base lg:text-lg font-semibold text-gray-700'>
                  {lockboxChecksSummaryQuery.data.numberOfTransactions}
                </div>
              </div>

              <div className='flex flex-col justify-start items-center'>
                <div className='font-lato tracking-wide text-[9.5px] uppercase text-gray-500'>
                  Bank Total
                </div>
                <div className='font-lato text-base lg:text-lg font-semibold text-gray-700 whitespace-nowrap'>
                  {currencyFormatter.format(lockboxChecksSummaryQuery.data.bank?.totalAmount ?? 0)}
                </div>
              </div>

              <div className='flex flex-col justify-start items-center'>
                <div className='font-lato tracking-wide text-[9.5px] uppercase text-gray-500'>
                  Check Total
                </div>
                <div className='flex flex-col items-center'>
                  <div className='font-lato text-base lg:text-lg font-semibold text-gray-700 whitespace-nowrap'>
                    {currencyFormatter.format(lockboxChecksSummaryQuery.data.totalBatchCheckAmount)}
                  </div>

                  {Math.abs(totalAmountDiff) > 0 && (
                    <div className='flex gap-1 items-center text-red-600'>
                      {totalAmountDiff > 0 ? (
                        <CaretDownOutlined className='text-sm' />
                      ) : (
                        <CaretUpOutlined className='text-sm' />
                      )}

                      <span className='text-sm font-semibold whitespace-nowrap'>
                        {currencyFormatter.format(Math.abs(totalAmountDiff))}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
      </div>

      <PageGrid<LockboxCheck>
        wrapperClassName='border border-slate-200 rounded-2xl overflow-hidden'
        contentClassName='!py-0'
        className='[&_.ant-table-tbody>tr>td]:!py-2 [&_.ant-table-tbody>tr>td]:![border-bottom:1px_solid_#e2e8f0] [&_.ant-table-tbody>tr:last-child>td]:![border-bottom:none_!important] [&_.ant-table-expanded-row>td]:![border-bottom:none_!important]'
        tableLayout='fixed'
        size='small'
        pagination={false}
        loading={lockboxChecksQuery.isLoading}
        columns={columns}
        dataSource={lockboxChecksQuery.data?.data ?? []}
        rowKey='id'
        scroll={{ x: 'max-content' }}
        expandable={{
          expandedRowRender: (check: LockboxCheck) => (
            <LockboxCheckExpandableRow checkId={check.id} />
          ),
          expandRowByClick: true,
          expandIcon: ExpandIcon
        }}
      />
    </PageContainer>
  )
}

const ExpandIcon = (props: RenderExpandIconProps<LockboxCheck>): JSX.Element | null => {
  if (!props.expandable) {
    return null
  }

  return (
    <button
      className={cx('-ml-1 p-2 flex items-center justify-center transition-opacity translate-x-2', {
        'opacity-50 hover:opacity-75': !props.expanded,
        'opacity-100': props.expanded
      })}
      onClick={e => {
        props.onExpand(props.record, e)
      }}
    >
      {props.expanded && <CaretDownOutlined />}

      {!props.expanded && <CaretRightOutlined />}
    </button>
  )
}
interface LockboxCheckExpandableRowProps {
  checkId: LockboxCheck['id']
}

const LockboxCheckExpandableRow = (props: LockboxCheckExpandableRowProps): JSX.Element => {
  const params = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()

  const dispatch = useAppDispatch()

  const handlePdfPreviewClick = useCallback(
    (file: Attachment) =>
      (event: React.MouseEvent<HTMLButtonElement>): void => {
        event.stopPropagation()

        dispatch(previewActions.open(file))
      },
    [dispatch]
  )

  const lockboxCheckDetailsQuery = useQuery<DetailedLockboxCheckResponse>({
    queryKey: [
      'lockbox-check-details',
      {
        checkId: props.checkId
      }
    ],

    queryFn: async ({ queryKey }) => {
      const { checkId } = queryKey[1] as typeof queryKey extends Array<string | infer T> ? T : never

      const result = await $api.get<DetailedLockboxCheckResponse>(`pinnacle/checks/${checkId}`)

      return result.data
    },

    enabled: params.id !== undefined
  })

  const user = useAppSelector(state => state.app.user)

  const hasAdminRole = useMemo(() => {
    return user.roles.includes(Role.Administrator)
  }, [user.roles])

  const token = window.localStorage.getItem('token')

  const buildCheckImagesHref = useCallback(() => {
    return `${API_URL}/pinnacle/checks/${props.checkId}/check-images/preview?date=${searchParams.get('date')}&token=${encodeURIComponent(token ?? '')}`
  }, [props.checkId, token, searchParams])

  const buildDocumentImagesHref = useCallback(() => {
    return `${API_URL}/pinnacle/checks/${props.checkId}/document-images/preview?date=${searchParams.get('date')}&token=${encodeURIComponent(token ?? '')}`
  }, [props.checkId, token, searchParams])

  const buildOriginsHref = useCallback(() => {
    return `${API_URL}/pinnacle/checks/${props.checkId}/origins/preview?date=${searchParams.get('date')}&token=${encodeURIComponent(token ?? '')}`
  }, [props.checkId, token, searchParams])

  const checkImagesCount = lockboxCheckDetailsQuery.data?.checkImagePaths?.length ?? 0
  const documentImagesCount = lockboxCheckDetailsQuery.data?.documentPaths?.length ?? 0

  return (
    <div className='px-6 pt-2 pb-4'>
      {lockboxCheckDetailsQuery.isLoading && <Spin className='w-full' />}

      {!lockboxCheckDetailsQuery.isLoading && lockboxCheckDetailsQuery.data && (
        <div className='flex flex-row flex-wrap gap-6'>
          {/* Images Section */}
          {(checkImagesCount > 0 || documentImagesCount > 0) && (
            <div className='flex flex-col gap-1'>
              <span className='text-[10px] font-semibold text-slate-500 uppercase tracking-wide'>
                Images
              </span>
              <div className='flex flex-row gap-2'>
                {/* Check Images */}
                {checkImagesCount > 0 && (
                  <button
                    type='button'
                    onClick={handlePdfPreviewClick({
                      mimeType: 'image/tiff',
                      name: 'Check Images',
                      href: buildCheckImagesHref()
                    })}
                    className='w-44 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg px-4 py-2 transition-colors text-left flex items-center gap-3'
                  >
                    <FileImageOutlined className='text-lg text-blue-600' />
                    <div className='flex flex-col'>
                      <span className='font-medium text-sm text-blue-900'>Check Images</span>
                      <span className='text-xs text-blue-600'>
                        {checkImagesCount} {checkImagesCount === 1 ? 'image' : 'images'}
                      </span>
                    </div>
                  </button>
                )}

                {/* Document Images */}
                {documentImagesCount > 0 && (
                  <button
                    type='button'
                    onClick={handlePdfPreviewClick({
                      mimeType: 'image/tiff',
                      name: 'Document Images',
                      href: buildDocumentImagesHref()
                    })}
                    className='w-44 bg-green-50 hover:bg-green-100 border border-green-200 rounded-lg px-4 py-2 transition-colors text-left flex items-center gap-3'
                  >
                    <FileImageOutlined className='text-lg text-green-600' />
                    <div className='flex flex-col'>
                      <span className='font-medium text-sm text-green-900'>Document Images</span>
                      <span className='text-xs text-green-600'>
                        {documentImagesCount} {documentImagesCount === 1 ? 'document' : 'documents'}
                      </span>
                    </div>
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Evidence Section */}
          {hasAdminRole && lockboxCheckDetailsQuery.data.extractName && (
            <div className='flex flex-col gap-1'>
              <span className='text-[10px] font-semibold text-slate-500 uppercase tracking-wide'>
                Evidence
              </span>
              <div className='flex flex-row gap-2'>
                <button
                  type='button'
                  onClick={handlePdfPreviewClick({
                    mimeType: 'text/xml',
                    name: 'XML Data',
                    href: buildOriginsHref()
                  })}
                  className='w-44 bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded-lg px-4 py-2 transition-colors text-left flex items-center gap-3'
                >
                  <FileTextOutlined className='text-lg text-slate-600' />
                  <div className='flex flex-col'>
                    <span className='font-medium text-sm text-slate-900'>XML Data</span>
                    <span className='text-xs text-slate-500 truncate max-w-[100px]'>
                      {lockboxCheckDetailsQuery.data.extractName}
                    </span>
                  </div>
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
